// Monthly Instalment Calculator – Girona (Catalonia, Spain)
// Counterpart of the Loan Simulator – Girona: same reference model, formulas and parameters.
// Returns the instalment of the fixed, mixed and variable products for a given loan.

import {
  GIRONA_LOAN_PARAMS,
  MAX_TERM_YEARS,
  MIN_TERM_YEARS,
  appraisalFee,
  ltvBandIndex,
  mixedPeriods,
  mixedRates,
  repaymentSchedule,
  tae,
} from "../loan-simulator/LoanSimulatorGironaCalc";

export { GIRONA_LOAN_PARAMS, MAX_TERM_YEARS, MIN_TERM_YEARS };

export const PRODUCTS = ["fixed", "mixed", "variable"];

function product(key, loan, n, periods, commissions) {
  const instalments = repaymentSchedule(loan, n, periods);
  const totalRepaid = instalments.reduce((sum, a) => sum + a, 0);
  return {
    key,
    initialRate: periods[0].rate,
    reviewRate: periods.length > 1 ? periods[1].rate : null,
    reviewFromMonth: periods.length > 1 ? periods[1].fromMonth : null,
    instalmentInitial: instalments[0],
    instalmentReview: periods.length > 1 ? instalments[periods[1].fromMonth - 1] : null,
    tae: tae(loan - commissions, instalments),
    totalRepaid,
    totalInterest: totalRepaid - loan,
  };
}

export function calculateInstalmentsGirona({
  loanAmount,
  termYears,
  propertyPrice,
  residence = "main",
  params = GIRONA_LOAN_PARAMS,
}) {
  const L = Number(loanAmount);
  const P = Number(propertyPrice);
  const T = Number(termYears);
  if (!(L > 0) || !(P > 0) || !Number.isInteger(T) || T < MIN_TERM_YEARS || T > MAX_TERM_YEARS) {
    return null;
  }

  const n = 12 * T;
  const ltv = L / P;
  const commissions = appraisalFee(P, params) + params.bankCommission * L;

  // Step 1 – rates of each product
  const rates = mixedRates(ltvBandIndex(ltv), residence === "second", params);
  const { fixedProduct, variableProduct } = params;
  const variablePeriods =
    n > variableProduct.initialMonths
      ? [
          { fromMonth: 1, rate: variableProduct.initialRate },
          { fromMonth: variableProduct.initialMonths + 1, rate: variableProduct.reviewRate },
        ]
      : [{ fromMonth: 1, rate: variableProduct.initialRate }];

  // Steps 2–4 – schedule, TAE and totals of each product
  const products = {
    // The fixed product is offered only for terms of 10 years or more
    fixed: T >= fixedProduct.minTermYears
      ? product("fixed", L, n, [{ fromMonth: 1, rate: fixedProduct.rate }], commissions)
      : null,
    mixed: product("mixed", L, n, mixedPeriods(rates, n, params), commissions),
    variable: product("variable", L, n, variablePeriods, commissions),
  };

  return {
    loanAmount: L,
    propertyPrice: P,
    termYears: T,
    termMonths: n,
    ltv,
    commissions,
    products,
    parameterVersion: params.version,
  };
}
